/**
 * ============================================================================
 * DAY VIEW v4.0 - PLANIVIO CSS CLASS-BASED
 * ============================================================================
 *
 * ✅ v4.0: CSS Class-Based Redesign
 *          - ALL inline styles → CSS classes (cal-day- namespace)
 *          - Timeline layout from planivio_calendar_views.html
 *          - Now-indicator with red dot
 *          - Dark mode ready via parent .calendar-redesign.dark
 *          - Drag & Drop + TimeSlot click preserved
 *
 * @version 4.0.0 - CSS Class-Based
 */

import React, { useRef, useEffect } from 'react';
import {
  weekDays,
  monthNames,
  isToday,
  getAppointmentsForDay,
  getFamilyMember
} from './calendarUtils';

const formatTimeCompact = (timeString) => {
  if (!timeString) return '';
  const parts = timeString.split(':');
  return `${parts[0]}:${parts[1]}`;
};

const getCategoryClass = (category) => {
  const map = {
    'arzt': 'medical', 'medical': 'medical',
    'schule': 'school', 'school': 'school',
    'arbeit': 'work', 'work': 'work',
    'privat': 'personal', 'personal': 'personal',
    'sport': 'sport',
    'familie': 'family', 'family': 'family',
    'sonstiges': 'other'
  };
  return map[category?.toLowerCase()] || 'default';
};

const getCategoryMeta = (category) => {
  const meta = {
    'arzt': { icon: '🏥', name: 'Gesundheit' },
    'medical': { icon: '🏥', name: 'Gesundheit' },
    'schule': { icon: '🎓', name: 'Schule' },
    'school': { icon: '🎓', name: 'Schule' },
    'arbeit': { icon: '💼', name: 'Arbeit' },
    'work': { icon: '💼', name: 'Arbeit' },
    'privat': { icon: '🏠', name: 'Privat' },
    'personal': { icon: '🏠', name: 'Privat' },
    'sport': { icon: '⚽', name: 'Sport' },
    'familie': { icon: '👨‍👩‍👧‍👦', name: 'Familie' },
    'family': { icon: '👨‍👩‍👧‍👦', name: 'Familie' },
    'sonstiges': { icon: '📌', name: 'Sonstiges' },
  };
  return meta[category?.toLowerCase()] || { icon: '📅', name: 'Termin' };
};

function DayView({ currentDate, appointments, onAppointmentClick, onAppointmentDrop, onTimeSlotClick }) {
  const dayAppointments = getAppointmentsForDay(appointments, currentDate);
  const timelineRef = useRef(null);
  const today = isToday(currentDate);
  const dayOfWeek = currentDate.getDay() === 0 ? 6 : currentDate.getDay() - 1;

  // Scroll to first event or current time on mount
  useEffect(() => {
    if (!timelineRef.current) return;
    const now = new Date();
    if (today) {
      const nowBlock = timelineRef.current.querySelector('.cal-day-now');
      if (nowBlock) {
        nowBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
    const firstCard = timelineRef.current.querySelector('.cal-day-event');
    if (firstCard) {
      setTimeout(() => firstCard.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    }
  }, [currentDate, today]);

  // Build time slots 07:00 – 22:00
  const hours = [];
  for (let h = 7; h <= 22; h++) {
    const hStr = String(h).padStart(2, '0') + ':00';
    const hourEvents = dayAppointments.filter(apt => {
      const eHour = parseInt((apt.time || '00:00').split(':')[0]);
      return eHour === h;
    });
    hours.push({ label: hStr, events: hourEvents, hour: h });
  }

  // Current hour for now-indicator
  const nowHour = new Date().getHours();
  const nowMin = new Date().getMinutes();

  // Drag handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('cal-day-slot--drag-over');
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('cal-day-slot--drag-over');
  };

  const handleDrop = (e, hour) => {
    e.preventDefault();
    e.currentTarget.classList.remove('cal-day-slot--drag-over');
    const appointmentId = e.dataTransfer.getData('appointmentId');
    if (appointmentId && onAppointmentDrop) {
      const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
      onAppointmentDrop(appointmentId, dateStr, `${String(hour).padStart(2, '0')}:00`);
    }
  };

  const handleDragStart = (e, apt) => {
    e.dataTransfer.setData('appointmentId', apt.id);
    e.currentTarget.classList.add('cal-day-event--dragging');
  };

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove('cal-day-event--dragging');
  };

  return (
    <div className="cal-day">

      {/* Header */}
      <div className="cal-day-header">
        <span className="cal-day-header-label">
          📄 {weekDays[dayOfWeek]}, {currentDate.getDate()}. {monthNames[currentDate.getMonth()]}
        </span>
        {today && <span className="cal-day-today-badge">Heute</span>}
        <span className="cal-day-header-count">
          {dayAppointments.length} {dayAppointments.length === 1 ? 'Termin' : 'Termine'}
        </span>
      </div>

      {/* Timeline */}
      <div className="cal-day-timeline" ref={timelineRef}>
        {hours.map(slot => {
          const isNowSlot = today && slot.hour === nowHour;

          return (
            <div
              key={slot.hour}
              className={`cal-day-block ${isNowSlot ? 'cal-day-block--now' : ''}`}
            >
              <div className="cal-day-time-label">{slot.label}</div>
              <div
                className="cal-day-slot"
                onClick={() => onTimeSlotClick && onTimeSlotClick(currentDate, `${String(slot.hour).padStart(2, '0')}:00`)}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, slot.hour)}
              >
                {/* Now indicator */}
                {isNowSlot && (
                  <div
                    className="cal-day-now"
                    style={{ top: `${(nowMin / 60) * 100}%` }}
                  />
                )}

                {/* Events */}
                {slot.events.map(apt => {
                  const catClass = getCategoryClass(apt.category);
                  const catMeta = getCategoryMeta(apt.category);
                  const member = getFamilyMember ? getFamilyMember(apt.person) : null;

                  return (
                    <div
                      key={apt.id}
                      className={`cal-day-event cal-day-event--${catClass}`}
                      onClick={(e) => { e.stopPropagation(); onAppointmentClick(apt); }}
                      draggable
                      onDragStart={(e) => handleDragStart(e, apt)}
                      onDragEnd={handleDragEnd}
                    >
                      <div className="cal-day-event-time">
                        {formatTimeCompact(apt.time)}{apt.endTime ? ` – ${formatTimeCompact(apt.endTime)}` : ''}
                      </div>
                      <div className="cal-day-event-title">
                        {apt.isRecurring && '🔁 '}{apt.title}
                      </div>
                      <div className="cal-day-event-sub">
                        {catMeta.icon} {catMeta.name}
                        {member && apt.person !== 'alle' && (
                          <span className="cal-day-event-person">
                            {member.avatar} {member.name}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default DayView;